import { Select, DatePicker } from 'antd';
import { useNotify } from 'react-admin';
import { makeStyles } from '@mui/styles';
import { useListContext } from 'ra-core';
import { useModalContext } from '@providers';
import React, { useEffect, useState } from 'react';

import { useRequest } from '@hooks';
import { cxInvoiceHttp } from '@network';
import { BlueButton, BlueOutlineButton } from '@components';
import { CreditInvoiceStatusType } from '@types';

const propsList: PropType[] = ['Finance Status', 'Fecha Timbrado'];
type PropType = 'Finance Status' | 'Fecha Timbrado';

export const CxInvoiceListActions = () => {
  const { selectedIds, refetch } = useListContext();
  const [prop, setProp] = useState<PropType>('Finance Status');
  const [hasError, setHasError] = useState(false);
  const { hideModal } = useModalContext();
  const notify = useNotify();
  const cls = useStyles();

  const [value, setValue] = useState<number | string>('');

  const resetValues = () => {
    setProp('Finance Status');
    setValue('');
    setHasError(false);
  };

  const onCancel = () => {
    resetValues();
    hideModal();
  };

  const onChangeProp = (v: PropType) => {
    setProp(v);
    setValue('');
    setHasError(false);
  };

  const { loading, submit } = useRequest(async () => {
    if (!prop) {
      return notify('No property is selected');
    }
    if (!Array.isArray(selectedIds) || selectedIds.length === 0) {
      return notify('No records are selected', { type: 'error' });
    }
    if (prop === 'Finance Status' && !value) {
      setHasError(true);
      return notify('No Finance Status is selected', { type: 'error' });
    }
    if (prop === 'Fecha Timbrado' && !value) {
      setHasError(true);
      return notify('No selected Date', { type: 'error' });
    }

    const updatedTotal = await cxInvoiceHttp.updateBulk({ selectedIds, prop, value });

    notify(`Updated ${updatedTotal} records`);
    onCancel();
    refetch();
  });

  useEffect(() => () => { resetValues(); }, []);

  return (
    <div className={cls.box}>
      <div className={cls.title}>Properties to update</div>
      <Select
        value={prop}
        onChange={onChangeProp}
        className={cls.select}
        placeholder="Select a property to edit">
        {propsList.map(propItem => (
          <Select.Option key={propItem} value={propItem}>{propItem}</Select.Option>
        ))}
      </Select>

      <div className={cls.content}>
        {prop && <div className={cls.title}>{prop}</div>}
        {prop === 'Finance Status' && (
          <Select
            value={value}
            onChange={v => setValue(v)}
            className={cls.select}
            placeholder="Select Finance Status"
            showSearch
            optionFilterProp="children"
            status={hasError ? 'error' : undefined}
          >
            <Select.Option value={CreditInvoiceStatusType.DENIED}>Denied</Select.Option>
          </Select>
        )}
        {prop === 'Fecha Timbrado' && (
          <DatePicker
            onChange={(_, v) => setValue(v)}
            className={cls.datePicker}
            status={hasError ? 'error' : undefined}
          />
        )}
      </div>

      <div className={cls.controls}>
        <BlueButton className={cls.btn} onClick={submit} disabled={loading}>
          Update
        </BlueButton>
        <BlueOutlineButton className={cls.btn} onClick={onCancel}>
          Cancel
        </BlueOutlineButton>
      </div>
    </div>
  );
};

const useStyles = makeStyles({
  box: {},
  group: {
    marginTop: 15,
  },
  title: {
    fontSize: 20,
    lineHeight: 1,
    fontWeight: 500,
  },
  select: {
    marginTop: 10,
    width: '100%',
    borderRadius: 8,
    border: '1px solid var(--background-color)',
    backgroundColor: 'var(--background-color)',
    '& .ant-select-selector': {
      backgroundColor: 'transparent !important',
    },
  },
  content: {
    marginTop: 15,
  },
  controls: {
    marginTop: 20,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  btn: {
    margin: '0 7px',
    padding: '5px 29px !important',
    height: 34,
    borderRadius: 8,
    textTransform: 'none',
  },
  datePicker: {
    border: 'none !important',
    marginTop: '20px !important',

    '& .ant-picker-input': {
      backgroundColor: 'var(--background-color)',
      border: '1px solid var(--background-color) !important',
      padding: '2px 8px !important',
      borderRadius: '2px !important',
    },
    '& input': {
      height: 28,
      border: 'none!important',
    },
    '&.ant-picker-status-error .ant-picker-input': {
      border: '1px solid red!important',
    },
  },
});
